// snapshot デコード結果の診断 (Sentry へ 1 load あたり最大 1 件の warning)。
//
// 背景 (2026-10): iPad で「長く描いた後、アプリをバックグラウンドにして再入室すると
// 絵の一部が欠ける (新規ロードなら全部出る)」報告。WebKit は canvas メモリが予算を
// 超えると **例外を出さずに透明ピクセルを返す / getContext が null を返す**ことがあり、
// 今は黙って欠けるだけで観測できない。ここは「欠けた疑いがある」ことを検出して
// 1 件だけ送る診断で、挙動 (描画結果) は変えない。
//
// 描画経路 (ペン / 合成) では一切呼ばない。load 時にレイヤー 1 枚あたり 1 回だけ。

import { captureWarning } from '../sentryCapture.js';

export type SnapshotLoadSource = 'welcome' | 'resume' | 'restore';

export interface LayerDiagnostic {
  readonly id: number;
  /** PNG の byte 数 (blob.size)。fetch 前に落ちたレイヤーは undefined。 */
  readonly pngBytes?: number;
  readonly width?: number;
  readonly height?: number;
  /** デコード (createImageBitmap + drawImage + getImageData) の所要 ms。 */
  readonly decodeMs?: number;
  /** PNG は十分大きいのに全画素 alpha=0 だった。 */
  readonly suspiciouslyEmpty?: boolean;
}

export interface SnapshotLoadDiagnostics {
  readonly layerCount: number;
  readonly layers: readonly LayerDiagnostic[];
  readonly droppedLayerIds: readonly number[];
  readonly totalDecodeMs: number;
}

/** 全画素 alpha=0 か。最初の非 0 alpha で即 return (普通の絵は先頭近くで抜ける)。 */
export const isAllTransparent = (rgba: Uint8ClampedArray): boolean => {
  for (let i = 3; i < rgba.length; i += 4) {
    if (rgba[i] !== 0) return false;
  }
  return true;
};

/**
 * 「空の PNG としては大きすぎる」byte 数のしきい値。
 * deflate の理論上の最大圧縮比は約 1032:1 なので、全透明 W×H の PNG は
 * 高々 W*H*4/1032 byte (2000² で約 15.5KB)。その約 2 倍 (= /500) を境にし、
 * 小さいキャンバスでの誤検知を避けるため下限 32KB を置く。
 */
export const suspiciousEmptyPngBytes = (width: number, height: number): number =>
  Math.max(32 * 1024, Math.ceil((width * height * 4) / 500));

/**
 * 「PNG は大きいのに全透明」の判定。安い順 (byte 数の比較 → alpha 走査) に評価するので、
 * 小さい PNG (= 本当に空のレイヤー) では走査自体をしない。
 */
export const isSuspiciouslyEmpty = (
  pngBytes: number,
  width: number,
  height: number,
  rgba: Uint8ClampedArray,
): boolean => pngBytes > suspiciousEmptyPngBytes(width, height) && isAllTransparent(rgba);

/** 報告するべきか (落ちたレイヤー or 疑わしい空レイヤーがある)。 */
export const shouldReportSnapshot = (d: SnapshotLoadDiagnostics): boolean =>
  d.droppedLayerIds.length > 0 || d.layers.some((l) => l.suspiciouslyEmpty === true);

const readDeviceMemory = (): number | undefined => {
  try {
    const m = (globalThis as { navigator?: { deviceMemory?: unknown } }).navigator?.deviceMemory;
    return typeof m === 'number' ? m : undefined;
  } catch {
    return undefined;
  }
};

/** 疑わしければ Sentry に 1 件だけ warning。Sentry 未 init でも throw しない。 */
export const reportSnapshotDiagnostics = (
  d: SnapshotLoadDiagnostics,
  source: SnapshotLoadSource,
  report: (message: string, extra: Record<string, unknown>) => void = captureWarning,
): void => {
  try {
    if (!shouldReportSnapshot(d)) return;
    const first = d.layers.find((l) => l.width !== undefined && l.height !== undefined);
    const deviceMemory = readDeviceMemory();
    report('snapshot decode suspicious', {
      source,
      layerCount: d.layerCount,
      droppedLayerIds: d.droppedLayerIds,
      emptyLayerIds: d.layers.filter((l) => l.suspiciouslyEmpty === true).map((l) => l.id),
      pngBytes: d.layers.map((l) => ({ id: l.id, bytes: l.pngBytes ?? null })),
      canvasSize: first === undefined ? null : { width: first.width, height: first.height },
      totalDecodeMs: Math.round(d.totalDecodeMs),
      layerDecodeMs: d.layers.map((l) => ({ id: l.id, ms: Math.round(l.decodeMs ?? 0) })),
      ...(deviceMemory === undefined ? {} : { deviceMemory }),
    });
  } catch {
    // best-effort
  }
};
